import { useCallback, useEffect, useState } from "react";
import Color from "@tiptap/extension-color";
import HardBreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

import EditorContent from "./EditorContent";
import EditorToolbar from "./EditorToolbar";
import { detectCurrentFormat } from "./helpers";

const TiptapEditor = ({
	content,
	onChange
}) => {
	const [currentFormat, setCurrentFormat] = useState("normal");

	// Handle editor content updates
	const handleEditorUpdate = useCallback(({ editor }) => {
		const html = editor.getHTML();

		const contentContainer = document.querySelector(".tiptap-content-container");
		if (contentContainer) {
			const isEmpty = !html || html === "<p></p>" || html === "<p><br></p>" || html.replace(/<[^>]*>/g, "").trim() === "";

			if (isEmpty) {
				contentContainer.classList.remove("has-content");
			} else {
				contentContainer.classList.add("has-content");
			}
		}

		if (onChange) {
			onChange(html);
		}
	}, [onChange]);

	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				heading: false,
				hardBreak: false, // Disable the default hardBreak to use our custom one
				paragraph: {
					HTMLAttributes: {
						class: "tiptap-paragraph",
					},
				},
			}),
			Underline,
			Highlight.configure({
				multicolor: true,
				HTMLAttributes: {
					class: "tiptap-highlight",
				},
			}),
			TextStyle,
			Color,
			TextAlign.configure({
				types: ["paragraph", "heading"],
				alignments: ["left", "center", "right", "justify"],
			}),
			Link.configure({
				openOnClick: false,
				HTMLAttributes: {
					class: "tiptap-link",
				},
			}),
			Heading.configure({
				levels: [1, 2, 3],
			}),
			Superscript,
			Subscript,
			HardBreak.configure({
				keepMarks: true,
				HTMLAttributes: {
					class: "line-break",
				},
			}),
		],

		content: content || "",
		onUpdate: handleEditorUpdate,
	});

	useEffect(() => {
		if (!editor) return;

		const updateCurrentFormat = () => {
			const format = detectCurrentFormat(editor);
			setCurrentFormat(format);
		};

		editor.on("selectionUpdate", updateCurrentFormat);
		editor.on("focus", updateCurrentFormat);
		editor.on("update", updateCurrentFormat);

		updateCurrentFormat();

		return () => {
			editor.off("selectionUpdate", updateCurrentFormat);
			editor.off("focus", updateCurrentFormat);
			editor.off("update", updateCurrentFormat);
		};
	}, [editor]);

	// Apply background styling on initialization for existing content
	// This ensures the proper styling is set when the editor loads, not just after updates
	useEffect(() => {
		if (!editor) return;

		// Run once on initialization to set the correct background for existing content
		const html = editor.getHTML();
		const contentContainer = document.querySelector(".tiptap-content-container");

		if (contentContainer) {
			const isEmpty = !html || html === "<p></p>" || html === "<p><br></p>" || html.replace(/<[^>]*>/g, "").trim() === "";

			if (isEmpty) {
				contentContainer.classList.remove("has-content");
			} else {
				contentContainer.classList.add("has-content");
			}
		}
	}, [editor]);

	if (!editor) {
		return null;
	}

	return (
		<>
			<EditorContent editor={editor}>
				<EditorToolbar
					editor={editor}
					currentFormat={currentFormat}
					setCurrentFormat={setCurrentFormat}
				/>
			</EditorContent>
		</>
	);
};

export default TiptapEditor;